/**
 * Wizard selling intent: which connected channels the operator wants this
 * product on, plus inline readiness (required vs recommended).
 *
 * Amazon/Temu adapters exist for later plug-in. 019C does not select, map, or
 * publish Amazon. Channel category is listing state, not Product Master.
 */

import { isAmazonProvider, evaluateAmazonAuReadiness } from "@/lib/providers/amazon-au";
import {
  connectorPriceRuleFromConfig,
  type ConnectorPriceRule,
  type WizardPriceOverride,
} from "@/lib/wizard-channel-price";
import { isAuthenticatedAuthStatus } from "@/lib/connector-status-ux";
import {
  builtinMarketplaceAdapters,
  evaluateCanonicalCompleteness,
  fieldPresent as canonicalFieldPresent,
  projectProviderReadiness,
  snapshotFromWizard,
  type CanonicalFieldKey,
  type CanonicalProductSnapshot,
} from "./marketplace-readiness";

export const WIZARD_STEP_KEYS = ["product", "variants", "selling", "review"] as const;
export type WizardStepKey = (typeof WIZARD_STEP_KEYS)[number];

const LEGACY_STEP_KEY: Record<string, WizardStepKey> = {
  type: "product",
  details: "product",
  extras: "product",
  product: "product",
  stock: "variants",
  variants: "variants",
  selling: "selling",
  review: "review",
};

export const WIZARD_SELECTABLE_PROVIDERS = ["ebay", "bigcommerce"] as const;

export type WizardRequirementLevel = "required" | "recommended";
export type WizardRequirementScope = "canonical" | "channel";

export interface WizardChannelIntent {
  connectorId: string;
  provider: string;
  displayName: string;
  selected: boolean;
  channelCategoryId: string | null;
  channelCategoryName: string | null;
  connectorPricing: ConnectorPriceRule;
  priceOverride: WizardPriceOverride | null;
  /**
   * Operator-chosen primary image for THIS account, drawn from the shared
   * product image library. null/absent means inherit the Product Master
   * primary image. The catalogue primary image is never rewritten by this.
   */
  primaryMediaAssetId?: string | null;
}

export interface WizardRequirement {
  key: string;
  label: string;
  level: WizardRequirementLevel;
  scope: WizardRequirementScope;
  field?: CanonicalFieldKey | "channel_category";
}

export interface WizardChannelReadiness {
  connectorId: string;
  provider: string;
  displayName: string;
  selected: boolean;
  status: "idle" | "ready" | "blocked";
  required: WizardRequirement[];
  recommended: WizardRequirement[];
  compact: string;
}

export interface WizardConnectorRow {
  id: string;
  name: string | null;
  provider: string;
  auth_status: string | null;
  health_status?: string | null;
  config?: unknown;
  last_sync_at?: string | null;
}

export function isWizardSelectableProvider(provider: string | null | undefined): boolean {
  const p = (provider ?? "").toLowerCase();
  return p === "ebay" || p === "bigcommerce";
}

/** Re-read result immediately before a wizard draft listing write. Wrong org fails closed. */
export function wizardConnectorSaveDecision(
  orgId: string,
  row: { id: string; org_id?: string | null; provider: string; auth_status: string | null; health_status?: string | null } | null,
): { save: true } | { save: false; error: string } {
  if (!row || row.org_id !== orgId) {
    return { save: false, error: "Connector is not available for this organisation" };
  }
  if (!connectorReadyForWizard({
    id: row.id,
    name: null,
    provider: row.provider,
    auth_status: row.auth_status,
    health_status: row.health_status ?? null,
  })) {
    return { save: false, error: "Connector needs authentication before a draft listing can be saved" };
  }
  return { save: true };
}

/** Publish-ready only from current auth. A connector row or an old success time is not enough. */
export function connectorReadyForWizard(row: WizardConnectorRow): boolean {
  if (!isWizardSelectableProvider(row.provider)) return false;
  if (!isAuthenticatedAuthStatus(row.auth_status)) return false;
  if ((row.health_status ?? "").trim() === "error") return false;
  return true;
}

export function wizardStepIndexFromKey(key: WizardStepKey): number {
  return WIZARD_STEP_KEYS.indexOf(key);
}

/** Resume 019B drafts (type/details/stock/extras/review) onto the 019C four-step wizard. */
export function resolveWizardStepIndex(opts: { rowStep?: string | null; payloadStep?: unknown }): number {
  const keyFromRow = LEGACY_STEP_KEY[String(opts.rowStep ?? "").trim()];
  if (keyFromRow) return wizardStepIndexFromKey(keyFromRow);
  if (typeof opts.payloadStep === "string") {
    const mapped = LEGACY_STEP_KEY[opts.payloadStep];
    if (mapped) return wizardStepIndexFromKey(mapped);
  }
  if (typeof opts.payloadStep === "number" && Number.isFinite(opts.payloadStep)) {
    // Legacy 5-step indices: 0 type, 1 details, 2 stock, 3 extras, 4 review.
    const legacy = Math.max(0, Math.min(4, Math.trunc(opts.payloadStep)));
    if (legacy <= 1 || legacy === 3) return 0;
    if (legacy === 2) return 1;
    return 3;
  }
  return 0;
}

export function mergeWizardSellingIntents(
  previous: WizardChannelIntent[] | null | undefined,
  connectors: WizardConnectorRow[],
): WizardChannelIntent[] {
  const prevById = new Map((previous ?? []).map((row) => [row.connectorId, row]));
  return connectors.filter(connectorReadyForWizard).map((row) => {
    const prev = prevById.get(row.id);
    return {
      connectorId: row.id,
      provider: row.provider,
      displayName: (row.name ?? "").trim() || (row.provider === "ebay" ? "eBay" : "BigCommerce"),
      selected: prev?.selected ?? false,
      channelCategoryId: prev?.channelCategoryId ?? null,
      channelCategoryName: prev?.channelCategoryName ?? null,
      connectorPricing: connectorPriceRuleFromConfig(row.config),
      priceOverride: prev?.priceOverride ?? null,
      primaryMediaAssetId: prev?.primaryMediaAssetId ?? null,
    };
  });
}

/**
 * Connector rows are only authoritative once the reference-data query has
 * settled AND the draft has finished hydrating.
 *
 * Loading order must never decide content: a draft that hydrates before the
 * connector query resolves must keep its stored intents. Merging against a
 * not-yet-loaded connector list would read "unknown" as "none available" and
 * erase valid draft state. An authoritative empty list is different — it really
 * means the selected connectors are gone, and merging is then correct.
 */
export function shouldMergeWizardSellingIntents(opts: {
  referenceLoaded: boolean;
  hydrationReady: boolean;
}): boolean {
  return opts.referenceLoaded && opts.hydrationReady;
}

export function toggleWizardChannel(intents: WizardChannelIntent[], connectorId: string, selected: boolean): WizardChannelIntent[] {
  return intents.map((row) => (row.connectorId === connectorId ? { ...row, selected } : row));
}

export function setWizardChannelPriceOverride(
  intents: WizardChannelIntent[],
  connectorId: string,
  priceOverride: WizardPriceOverride | null,
): WizardChannelIntent[] {
  return intents.map((row) => (row.connectorId === connectorId ? { ...row, priceOverride } : row));
}

export function setWizardChannelCategory(
  intents: WizardChannelIntent[],
  connectorId: string,
  categoryId: string | null,
  categoryName: string | null,
): WizardChannelIntent[] {
  return intents.map((row) =>
    row.connectorId === connectorId
      ? { ...row, channelCategoryId: categoryId, channelCategoryName: categoryName }
      : row,
  );
}

function channelRequiresMarketplaceCategory(provider: string): boolean {
  return provider.toLowerCase() === "ebay";
}

/** Blank and whitespace are missing. A stored null must not read as ready. */
export function wizardCategoryIdPresent(categoryId: string | null | undefined): boolean {
  return typeof categoryId === "string" && categoryId.trim().length > 0;
}

/** One picker per selected eBay account that still has no marketplace category. */
export function ebayIntentsMissingCategory(intents: WizardChannelIntent[]): WizardChannelIntent[] {
  return intents.filter((intent) =>
    intent.selected
    && channelRequiresMarketplaceCategory(intent.provider)
    && !wizardCategoryIdPresent(intent.channelCategoryId),
  );
}

/**
 * Category sent with one connector's draft listing.
 * A missing category stays null on that connector and is not copied from another account.
 */
export function wizardDraftListingCategory(intent: Pick<WizardChannelIntent, "connectorId" | "channelCategoryId" | "channelCategoryName">): {
  connectorId: string;
  channelCategoryId: string | null;
  channelCategoryName: string | null;
} {
  const categoryId = intent.channelCategoryId?.trim() ?? "";
  if (!categoryId) {
    return { connectorId: intent.connectorId, channelCategoryId: null, channelCategoryName: null };
  }
  const name = intent.channelCategoryName?.trim() ?? "";
  return {
    connectorId: intent.connectorId,
    channelCategoryId: categoryId,
    channelCategoryName: name.length > 0 ? name : null,
  };
}

/**
 * Product and variant steps validate their own fields.
 * Selling does not block Continue to review: an incomplete channel stays
 * visible as not publish-ready, and Create Product still saves Tradivo only.
 */
export function wizardCanLeaveStep(step: WizardStepKey, gates: { product: boolean; variants: boolean }): boolean {
  if (step === "product") return gates.product;
  if (step === "variants") return gates.variants;
  return true;
}

/** Copy the intents that are on screen so a later edit cannot change a save already queued. */
export function captureWizardSellingIntents(intents: WizardChannelIntent[]): WizardChannelIntent[] {
  return intents.map((intent) => ({
    ...intent,
    connectorPricing: {
      ...intent.connectorPricing,
      config: { ...intent.connectorPricing.config },
    },
    priceOverride: intent.priceOverride ? { ...intent.priceOverride } : null,
  }));
}

/**
 * What the Review screen says this account's draft listing will contain.
 * Listing scope and marketplace category are listing state, not Product Master,
 * so they are shown per account. A missing category is named rather than
 * hidden, because it is what blocks publication for that account only.
 */
export function wizardListingConfigSummary(input: {
  provider: string;
  variantCount: number;
  channelCategoryName: string | null;
}): string {
  const ebay = (input.provider ?? "").toLowerCase() === "ebay";
  const kind = ebay ? "eBay listing" : "BigCommerce listing";
  const scope = input.variantCount > 1 ? `${input.variantCount} variants` : "single SKU";
  const category = (input.channelCategoryName ?? "").trim();
  if (category) return `${kind} · ${scope} · Category: ${category}`;
  return ebay
    ? `${kind} · ${scope} · Marketplace category not chosen`
    : `${kind} · ${scope} · Category not chosen (optional)`;
}

/**
 * Selling and Review must evaluate the same intents the draft save captured.
 * The copy is taken from the intents rendered on Selling, not from a later edit.
 */
export function wizardSellingLeavePayload(rendered: WizardChannelIntent[]): {
  sellingIntents: WizardChannelIntent[];
  stepKey: "review";
} {
  return { sellingIntents: captureWizardSellingIntents(rendered), stepKey: "review" };
}

export function evaluateWizardChannelReadiness(
  intent: WizardChannelIntent,
  snapshot: CanonicalProductSnapshot,
): WizardChannelReadiness {
  if (!intent.selected) {
    return {
      connectorId: intent.connectorId,
      provider: intent.provider,
      displayName: intent.displayName,
      selected: false,
      status: "idle",
      required: [],
      recommended: [],
      compact: `${intent.displayName} — not selected`,
    };
  }

  const adapter = builtinMarketplaceAdapters.find((a) => a.provider === intent.provider);
  const required: WizardRequirement[] = [];
  if (isAmazonProvider(intent.provider)) {
    const projected = evaluateAmazonAuReadiness({ connected: true });
    required.push({
      key: `${intent.connectorId}:amazon_ptd`,
      label: projected.summary,
      level: "required",
      scope: "channel",
    });
  } else if (adapter) {
    const projected = projectProviderReadiness({
      adapter,
      product: snapshot,
      connected: true,
      evaluated: intent.provider !== "temu",
    });
    for (const missing of projected.missingFields) {
      required.push({
        key: `${intent.connectorId}:${missing.field}`,
        label: missing.label,
        level: "required",
        scope: "canonical",
        field: missing.field,
      });
    }
  }
  if (channelRequiresMarketplaceCategory(intent.provider) && !wizardCategoryIdPresent(intent.channelCategoryId)) {
    required.push({
      key: `${intent.connectorId}:channel_category`,
      label: `Marketplace category (${intent.displayName})`,
      level: "required",
      scope: "channel",
      field: "channel_category",
    });
  }

  const canonical = evaluateCanonicalCompleteness(snapshot);
  const requiredFields = new Set(required.map((r) => r.field));
  const recommended: WizardRequirement[] = canonical.gaps
    .filter((g) => !requiredFields.has(g.field))
    .map((g) => ({
      key: `${intent.connectorId}:rec:${g.field}`,
      label: g.label,
      level: "recommended" as const,
      scope: "canonical" as const,
      field: g.field,
    }));

  const blocked = required.length > 0;
  const compact = blocked
    ? `⚠ ${intent.displayName} needs ${required.length} field${required.length === 1 ? "" : "s"}`
    : `✓ ${intent.displayName} ready`;

  return {
    connectorId: intent.connectorId,
    provider: intent.provider,
    displayName: intent.displayName,
    selected: true,
    status: blocked ? "blocked" : "ready",
    required,
    recommended,
    compact,
  };
}

export function evaluateWizardSelling(
  intents: WizardChannelIntent[],
  snapshot: CanonicalProductSnapshot,
): WizardChannelReadiness[] {
  return intents.map((intent) => evaluateWizardChannelReadiness(intent, snapshot));
}

/** One blocked channel must not mark a ready sibling as blocked. */
export function channelReadinessIsIsolated(rows: WizardChannelReadiness[]): boolean {
  const selected = rows.filter((r) => r.selected);
  if (selected.length < 2) return true;
  const blocked = selected.filter((r) => r.status === "blocked");
  const ready = selected.filter((r) => r.status === "ready");
  if (blocked.length === 0 || ready.length === 0) return true;
  return ready.every((r) => r.required.length === 0);
}

export function recommendedDoesNotBlock(rows: WizardChannelReadiness[]): boolean {
  return rows.filter((r) => r.selected).every((r) => r.recommended.every((req) => req.level === "recommended") && (r.status === "ready" || r.required.length > 0));
}

export function wizardSnapshotFromForm(
  form: Parameters<typeof snapshotFromWizard>[0],
  imageCount: number,
): CanonicalProductSnapshot {
  return snapshotFromWizard(form, imageCount);
}

export function fieldStillMissing(snapshot: CanonicalProductSnapshot, field: CanonicalFieldKey): boolean {
  return !canonicalFieldPresent(snapshot, field);
}

export function uniqueRequiredFixes(rows: WizardChannelReadiness[]): WizardRequirement[] {
  const seen = new Set<string>();
  const out: WizardRequirement[] = [];
  for (const row of rows) {
    if (!row.selected) continue;
    for (const req of row.required) {
      const id = `${req.scope}:${req.field ?? req.key}`;
      if (seen.has(id) && req.field !== "channel_category") continue;
      if (req.field === "channel_category") {
        if (seen.has(req.key)) continue;
        seen.add(req.key);
      } else {
        seen.add(id);
      }
      out.push(req);
    }
  }
  return out;
}

export function uniqueRecommendedFixes(rows: WizardChannelReadiness[], snapshot: CanonicalProductSnapshot): WizardRequirement[] {
  const requiredKeys = new Set(uniqueRequiredFixes(rows).map((r) => r.field));
  return evaluateCanonicalCompleteness(snapshot)
    .gaps.filter((g) => !requiredKeys.has(g.field))
    .map((g) => ({
      key: `rec:${g.field}`,
      label: g.label,
      level: "recommended" as const,
      scope: "canonical" as const,
      field: g.field,
    }));
}

export function selectedReadyCount(rows: WizardChannelReadiness[]): { ready: number; blocked: number; selected: number } {
  const selected = rows.filter((r) => r.selected);
  return {
    selected: selected.length,
    ready: selected.filter((r) => r.status === "ready").length,
    blocked: selected.filter((r) => r.status === "blocked").length,
  };
}
